import React from 'react';
import { 
  ResponsiveContainer, Tooltip,
  ComposedChart, Area, XAxis, YAxis, CartesianGrid, Legend,
  BarChart, Bar, Cell
} from 'recharts';
import { Users } from 'lucide-react';

/**
 * Componente "Horas por Equipo"
 * Layout: 1/3 Pie Chart (totales) + 2/3 Gráfica de áreas por día
 * SOLUCIÓN DEFINITIVA: ComposedChart sin stackId
 * MEJORADO: Leyenda clicable como filtro
 */
export default function HorasPorEquipo({
  resumenEquipoData = [],
  horasPorDiaData = [],
  isDarkMode = true,
  onEquipoClick = null,
  selectedEquipos = []
}) {
  // Estilos
  const cardClasses = isDarkMode
    ? 'bg-[#071a0f]/80 backdrop-blur-md border border-[#00FF66]/20 rounded-xl shadow-lg hover:border-[#00FF66]/40 hover:shadow-[0_0_24px_rgba(0,255,102,0.14)] transition-all duration-300'
    : 'bg-white border border-emerald-200 rounded-xl shadow-lg';

  const textPrimary = isDarkMode ? 'text-[#e8fff0]' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-[#a0c8b0]' : 'text-gray-600';
  const textMuted = isDarkMode ? 'text-[#7ea58f]' : 'text-gray-500';

  const TEAM_COLORS = {
    finops: '#00FF66',
    f2f: '#7B3FF2',
    ops: '#7B3FF2',
    femsa: '#19B8C9',
    fensa: '#19B8C9',
    'servicios profesionales': '#A56EFF',
    alianzas: '#00B8FF',
    innovation: '#FF4D8D'
  };

  const FALLBACK_COLORS = ['#1EEB8A', '#34D399', '#00D4AA', '#2DD4BF'];

  const getEquipoColor = (equipo, index = 0) => {
    const rawKey = (equipo || '').toLowerCase().trim();

    if (rawKey.includes('finops')) return TEAM_COLORS.finops;
    if (rawKey.includes('f2f') || rawKey.includes('ops')) return TEAM_COLORS.f2f;
    if (rawKey.includes('femsa') || rawKey.includes('fensa')) return TEAM_COLORS.femsa;
    if (rawKey.includes('servicios profesionales')) return TEAM_COLORS['servicios profesionales'];
    if (rawKey.includes('alianzas')) return TEAM_COLORS.alianzas;
    if (rawKey.includes('innovation') || rawKey.includes('innovacion')) return TEAM_COLORS.innovation;

    return TEAM_COLORS[rawKey] || FALLBACK_COLORS[index % FALLBACK_COLORS.length];
  };

  // Preparar datos del bloque lateral (barras)
  const dataEquipo = resumenEquipoData.map(item => ({
    name: item.equipo || 'Sin equipo',
    value: parseFloat(item.horas) || 0,
    color: getEquipoColor(item.equipo)
  }));

  const CustomTooltipPie = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      const total = data.payload.totalHoras || 0;
      const percent = total > 0 ? (data.value / total) * 100 : 0;
      
      return (
        <div className={`${
          isDarkMode 
            ? 'bg-[#03120a]/95 border-2 border-[#00FF66]/40' 
            : 'bg-white/95 border-2 border-emerald-300'
        } backdrop-blur-md rounded-lg p-3 shadow-xl`}>
          <div className={`${textPrimary} font-semibold text-sm`}>{data.name}</div>
          <div className={`${textSecondary} text-sm`}>
            {Number(data.value || 0).toLocaleString('es-MX', { maximumFractionDigits: 2 })} horas
          </div>
          <div className={`${textMuted} text-xs`}>
            {percent.toFixed(1)}%
          </div>
        </div>
      );
    }
    return null;
  };

  const CustomTooltipLine = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className={`${
          isDarkMode 
            ? 'bg-[#03120a]/95 border-2 border-[#00FF66]/40' 
            : 'bg-white/95 border-2 border-emerald-300'
        } backdrop-blur-md rounded-lg p-3 shadow-xl`}>
          <div className={`${textPrimary} font-semibold text-sm mb-2`}>{label}</div>
          {payload.map((entry, index) => (
            <div key={index} className="flex items-center gap-2 mb-1">
              <div 
                className="w-3 h-3 rounded"
                style={{ backgroundColor: entry.color }}
              />
              <span className={`${textSecondary} text-xs`}>
                {entry.name}: {entry.value.toFixed(1)} hrs
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  const CustomLegendPie = ({ data, onItemClick = null, selectedValues = [] }) => {
    const total = data.reduce((sum, item) => sum + item.value, 0);

    const handleLegendItemClick = (itemName) => {
      if (onItemClick && typeof onItemClick === 'function') {
        onItemClick(itemName);
      }
    };

    return (
      <div className="flex flex-col gap-2 mt-3">
        {data.map((entry, index) => {
          const percent = total > 0 ? (entry.value / total) * 100 : 0;
          const isSelected = selectedValues.includes(entry.name);
          
          return (
            <button
              key={`legend-${index}`}
              type="button"
              onClick={() => handleLegendItemClick(entry.name)}
              className={`flex items-center gap-2 p-2 rounded-lg transition-all active:scale-95 ${
                onItemClick ? 'cursor-pointer' : 'cursor-default'
              } ${
                isSelected 
                  ? isDarkMode 
                    ? 'bg-[#00FF66]/20 border border-[#00FF66]/40' 
                    : 'bg-emerald-100 border border-emerald-400'
                  : isDarkMode
                    ? 'hover:bg-[#00FF66]/10'
                    : 'hover:bg-emerald-50'
              }`}
            >
              <div 
                className="w-3 h-3 rounded flex-shrink-0"
                style={{ backgroundColor: entry.color || getEquipoColor(entry.name, index) }}
              />
              <div className="flex-1">
                <div className={`${textPrimary} text-xs font-medium`}>
                  {entry.name}
                </div>
                <div className={`${textMuted} text-xs`}>
                  {entry.value.toLocaleString('es-MX', { maximumFractionDigits: 1 })} hrs · {percent.toFixed(1)}%
                </div>
              </div>
            </button>
          );
        })}
      </div>
    );
  };

  const total = dataEquipo.reduce((sum, item) => sum + item.value, 0);
  const dataWithTotal = dataEquipo.map(item => ({ ...item, totalHoras: total }));

  return (
    <div className={`${cardClasses} p-4 mt-4`}>
      <h3 className={`${isDarkMode ? 'text-[#00FF66]' : textPrimary} text-lg font-bold mb-4 flex items-center gap-2`}>
        <span
          className="inline-flex items-center justify-center w-7 h-7 rounded-md border"
          style={{
            backgroundColor: isDarkMode ? 'rgba(165,110,255,0.15)' : 'rgba(165,110,255,0.1)',
            borderColor: isDarkMode ? 'rgba(165,110,255,0.4)' : 'rgba(165,110,255,0.35)'
          }}
        >
          <Users width={15} height={15} color="#A56EFF" />
        </span>
        Horas por Equipo
      </h3>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Barras horizontales por equipo */}
        <div className="lg:col-span-1 flex flex-col">
          <h4 className={`${textSecondary} text-sm font-semibold mb-3 text-center`}>
            Total por Equipo
          </h4>
          
          {dataWithTotal.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className={`${textMuted} text-sm`}>No hay datos</div>
            </div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart
                  data={dataWithTotal}
                  layout="vertical"
                  margin={{ top: 10, right: 10, left: 10, bottom: 10 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke={isDarkMode ? '#0e3d25' : '#E5E7EB'}
                    horizontal={false}
                  />
                  <XAxis
                    type="number"
                    stroke={isDarkMode ? '#88b79d' : '#6B7280'}
                    tick={{ fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={90}
                    stroke={isDarkMode ? '#88b79d' : '#6B7280'}
                    tick={{ fontSize: 10 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip content={<CustomTooltipPie />} cursor={{ fill: 'rgba(0,255,102,0.08)' }} />
                  <Bar dataKey="value" radius={[0, 8, 8, 0]}>
                    {dataWithTotal.map((entry, index) => (
                      <Cell key={`bar-${index}`} fill={entry.color || getEquipoColor(entry.name, index)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <CustomLegendPie
                data={dataWithTotal}
                onItemClick={onEquipoClick}
                selectedValues={selectedEquipos}
              />
            </>
          )}
        </div>

        {/* Area Chart - SIN STACKID */}
        <div className="lg:col-span-2 flex flex-col">
          <h4 className={`${textSecondary} text-sm font-semibold mb-3`}>
            Evolución por Día
          </h4>
          
          {horasPorDiaData.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className={`${textMuted} text-sm`}>No hay datos de evolución diaria</div>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={320}>
              <ComposedChart
                data={horasPorDiaData}
                margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
              >
                <defs>
                  {resumenEquipoData.map((equipo, index) => (
                    <linearGradient 
                      key={`gradient-${index}`} 
                      id={`colorEquipo${index}`} 
                      x1="0" 
                      y1="0" 
                      x2="0" 
                      y2="1"
                    >
                      <stop 
                        offset="5%" 
                        stopColor={getEquipoColor(equipo.equipo, index)} 
                        stopOpacity={0.6}
                      />
                      <stop 
                        offset="95%" 
                        stopColor={getEquipoColor(equipo.equipo, index)} 
                        stopOpacity={0.05}
                      />
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid 
                  strokeDasharray="3 3" 
                  stroke={isDarkMode ? '#0e3d25' : '#E5E7EB'} 
                />
                <XAxis 
                  dataKey="fecha" 
                  stroke={isDarkMode ? '#88b79d' : '#6B7280'}
                  style={{ fontSize: '12px' }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis 
                  stroke={isDarkMode ? '#88b79d' : '#6B7280'}
                  style={{ fontSize: '12px' }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip content={<CustomTooltipLine />} />
                <Legend 
                  wrapperStyle={{
                    fontSize: '12px',
                    color: isDarkMode ? '#d9d9d9' : '#000'
                  }}
                />
                {resumenEquipoData.map((equipo, index) => (
                  <Area
                    key={`area-${index}`}
                    type="monotone"
                    dataKey={equipo.equipo}
                    stroke={getEquipoColor(equipo.equipo, index)}
                    strokeWidth={2}
                    fill={`url(#colorEquipo${index})`}
                  />
                ))}
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
}